import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Sparkles, DollarSign, Calendar, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => {
  if (!n && n !== 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
};

const relativeWhen = (iso) => {
  if (!iso) return "recent";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.max(0, Math.floor((now - then) / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? "" : "s"} ago`;
  return `${Math.floor(days / 30)} month${days < 60 ? "" : "s"} ago`;
};

export default function PublicWinWall() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reactivation/public/wins`).then((r) => {
      setData(r.data);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  const wins = data?.wins || [];
  const totals = data?.totals || {};
  const usingSamples = data?.using_samples;

  return (
    <div className="min-h-screen bg-[#FFF6EE] text-[#0A192F]" data-testid="win-wall-page">
      {/* Top nav */}
      <div className="border-b border-[#E5D9CB] bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight no-underline text-[#0A192F]">
            Clients<span className="text-[#E24E1B]">OnRamp</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/pricing-public" className="text-neutral-600 hover:text-black no-underline">Pricing</Link>
            <Link to="/book" data-testid="win-wall-book-cta"
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 no-underline hover:bg-[#E24E1B] transition">
              Book a call <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
        <div className="inline-flex items-center gap-2 bg-white border border-[#E5D9CB] px-3 py-1.5 text-xs font-mono uppercase tracking-widest mb-6">
          <TrendingUp className="w-3.5 h-3.5 text-[#E24E1B]" /> Reactivation win wall
        </div>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-[1.05] mb-6 max-w-3xl">
          Real revenue,<br />recovered from old databases.
        </h1>
        <p className="text-neutral-600 max-w-2xl text-lg leading-relaxed">
          Every card below is money one of our clients earned back from a lead who <em>already existed</em> in
          their database — reactivated by our AI. No new ads. No cold outreach. Just quiet outbound to warm
          contacts that had gone dark.
        </p>

        {/* Stat band */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          <StatCard icon={DollarSign} label="Total recovered" value={money(totals.recovered_all_time)} accent />
          <StatCard icon={Calendar}   label="Bookings · last 30d" value={totals.appointments_last_30d ?? 0} />
          <StatCard icon={Sparkles}   label="Active campaigns"     value={totals.campaigns_active ?? 0} />
          <StatCard icon={TrendingUp} label="Avg ticket per win"   value={money(totals.avg_ticket)} />
        </div>
      </div>

      {/* Wins grid */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {usingSamples && (
          <div className="mb-6 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs" data-testid="win-wall-samples-banner">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-amber-900">
              Showing <strong>industry benchmarks</strong> until your first campaign lands its first booking. Real wins appear here automatically the moment an AI-reactivated lead books.
            </span>
          </div>
        )}

        {loading ? (
          <div className="text-neutral-500 py-16 text-center">Loading wins…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="win-wall-grid">
            {wins.map((w, i) => (
              <div
                key={i}
                data-testid={`win-card-${i}`}
                className="bg-white border border-[#E5D9CB] p-5 hover:shadow-lg transition group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                    {w.industry || "Local business"}
                  </span>
                  {!w.sample && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                      LIVE
                    </span>
                  )}
                </div>
                <div className="font-display text-3xl font-black text-[#E24E1B] mb-2 group-hover:scale-105 transition-transform origin-left">
                  {money(w.revenue)}
                </div>
                <div className="text-sm font-semibold mb-1">
                  {w.first_name} booked {w.service}
                </div>
                <div className="text-xs text-neutral-500">
                  {[w.city, w.when && relativeWhen(w.when)].filter(Boolean).join(" · ") || (w.sample ? "Industry benchmark" : "recently")}
                </div>
              </div>
            ))}

            {/* CTA card — always last */}
            <Link
              to="/book"
              data-testid="win-wall-cta-card"
              className="bg-[#0A192F] text-white p-5 border border-[#0A192F] hover:bg-[#E24E1B] hover:border-[#E24E1B] transition flex flex-col justify-between group no-underline"
            >
              <div>
                <div className="text-xs font-mono uppercase tracking-widest opacity-70 mb-3">
                  Your business, next
                </div>
                <div className="font-display text-2xl font-black leading-tight mb-3">
                  Turn your dormant database into next month's revenue.
                </div>
                <div className="text-sm opacity-80 leading-relaxed">
                  Book a 20-minute call — we'll audit your customer list live and show you exactly what could come back.
                </div>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 font-semibold text-sm border-b border-white/30 pb-0.5 w-fit group-hover:border-white transition">
                Get your audit <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
        )}
      </div>

      {/* Bottom band */}
      <div className="max-w-6xl mx-auto px-6 pb-24 pt-6">
        <div className="bg-white border border-[#E5D9CB] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl font-bold mb-1">Zero risk. Pay only for booked appointments.</div>
            <p className="text-sm text-neutral-600 max-w-xl">
              We charge a flat fee per booked appointment — not per lead, not per message, not per month.
              If we don't recover revenue, you don't pay.
            </p>
          </div>
          <Link to="/book" data-testid="win-wall-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#E24E1B] text-white px-6 py-3 font-semibold no-underline hover:bg-[#0A192F] transition whitespace-nowrap">
            Book my audit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-[#E5D9CB]"}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}
